import styled from "@emotion/styled";
import type { ReactNode } from "react";

import { Icon } from "@/components/atoms/Icon";

type Positions = "vertical" | "horizontal";
type CardContainerProps = { position: Positions };

interface ActionCardProps {
  title: string;
  description: string;
  icon?: ReactNode;
  actionIcon?: ReactNode;
  actionLabel?: string;
  onAction?: () => void;
  position?: Positions;
}

const CardContainer = styled.div<CardContainerProps>`
  background: linear-gradient(
    180deg,
    ${(props) => props.theme.colors.backgroundLinearDegradado.linear.start},
    ${(props) => props.theme.colors.backgroundLinearDegradado.linear.end}
  );
  box-shadow: ${(props) => props.theme.shadows.cardInset};
  border-radius: ${(props) => props.theme.shape.borderRadiusLarge};
  padding: ${(props) => props.theme.spacing(3)};
  display: flex;
  flex-direction: ${(props) =>
    props.position === "vertical" ? "column" : "row"};
  align-items: flex-start;
  gap: ${(props) => props.theme.spacing(3)};
  transition: all 0.2s ease;
`;

const IconWrapper = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #5680e9;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const InfoContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing(1)};
  min-width: 0;
  white-space: normal;
  overflow-wrap: break-word;
`;

const Title = styled.span`
  color: ${(props) => props.theme.colors.text.highEmphasis};
  font-family: ${(props) => props.theme.typography.body3Bold.fontFamily};
  font-size: ${(props) => props.theme.typography.body3Bold.fontSize};
  font-weight: ${(props) => props.theme.typography.body3Bold.fontWeight};
  line-height: ${(props) => props.theme.typography.body3Bold.lineHeight};
  word-break: break-word;
`;

const Description = styled.span`
  color: ${(props) => props.theme.colors.text.mediumLowEmphasis};
  font-family: ${(props) => props.theme.typography.body4Regular.fontFamily};
  font-size: ${(props) => props.theme.typography.body4Regular.fontSize};
  font-weight: ${(props) => props.theme.typography.body4Regular.fontWeight};
  line-height: ${(props) => props.theme.typography.body4Regular.lineHeight};
`;

const ActionButton = styled.button`
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing(1)};
  color: ${(props) => props.theme.colors.accent.A[600]};
  font-family: ${(props) => props.theme.typography.body4Bold.fontFamily};
  font-size: ${(props) => props.theme.typography.body4Bold.fontSize};
  font-weight: ${(props) => props.theme.typography.body4Bold.fontWeight};
  flex-shrink: 0;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }

  &:active {
    opacity: 0.6;
  }
`;

export const ActionCard = ({
  title,
  description,
  icon,
  actionIcon,
  actionLabel,
  onAction,
  position = "horizontal",
}: ActionCardProps) => {
  return (
    <CardContainer position={position}>
      {icon && <IconWrapper>{icon}</IconWrapper>}
      <InfoContainer>
        <Title>{title}</Title>
        <Description>{description}</Description>
        {(actionLabel || onAction) && (
          <ActionButton onClick={onAction}>
            {actionLabel}
            {actionIcon ?? <Icon name="play" size={18} />}
          </ActionButton>
        )}
      </InfoContainer>
    </CardContainer>
  );
};
